(() => {
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const modulo = (value, length) => ((value % length) + length) % length;

  function setup(root) {
    if (root.dataset.rpTextReviewsReady === 'true') return;
    root.dataset.rpTextReviewsReady = 'true';
    const track = root.querySelector('[data-rp-review-track]');
    const previous = root.querySelector('[data-rp-review-prev]');
    const next = root.querySelector('[data-rp-review-next]');
    if (!track || !previous || !next) return;
    const originals = [...track.querySelectorAll('.rp-text-reviews__card')];
    const count = originals.length;
    if (count < 2) return;
    const listeners = new AbortController();
    originals.forEach((card, index) => { card.dataset.rpTextReviewIndex = String(index); });
    const cloneSet = side => originals.map((card, position) => {
      const clone = card.cloneNode(true);
      const suffix = '--' + side + '-' + position;
      clone.querySelectorAll('[id]').forEach(element => { element.id += suffix; });
      clone.querySelectorAll('[aria-controls]').forEach(element => {
        element.setAttribute('aria-controls', element.getAttribute('aria-controls') + suffix);
      });
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a,button,[tabindex]').forEach(element => { element.tabIndex = -1; });
      return clone;
    });
    track.prepend(...cloneSet('before'));
    track.append(...cloneSet('after'));
    const allCards = [...track.querySelectorAll('.rp-text-reviews__card')];
    const cardGroups = originals.map(() => []);
    allCards.forEach(card => {
      cardGroups[Number(card.dataset.rpTextReviewIndex)].push({
        card,
        copy: card.querySelector('[data-rp-review-copy]'),
        button: card.querySelector('[data-rp-review-more]')
      });
    });
    const originalCopies = originals.map(card => card.querySelector('[data-rp-review-copy]'));
    let visible = false;
    let hovering = false;
    let touching = false;
    let focused = false;
    let moving = false;
    let resumeAt = 0;
    let playTimer = 0;
    let moveFrame = 0;
    let manualTimer = 0;
    let layoutFrame = 0;
    let cycleStart = 0;
    let cycleWidth = 0;
    let lastWritten = 0;
    let nativeScrolling = false;
    let disposed = false;
    let observer;

    // The equal repeated sets must rebase instantly, without CSS snap correction.
    track.style.scrollBehavior = 'auto';
    track.style.scrollSnapType = 'none';
    track.style.overflowAnchor = 'none';
    const position = index => allCards[index].getBoundingClientRect().left - allCards[0].getBoundingClientRect().left;
    const stride = () => position(1);
    const writePosition = value => {
      track.scrollLeft = cycleWidth ? cycleStart + modulo(value - cycleStart, cycleWidth) : value;
      lastWritten = track.scrollLeft;
    };
    const expanded = () => originalCopies.some(copy => copy.classList.contains('is-expanded'));
    const canPlay = () => !disposed && root.isConnected && visible && !document.hidden && !motionQuery.matches && !hovering && !touching && !focused && !moving && !nativeScrolling && !expanded();
    const stopPlay = () => { clearTimeout(playTimer); playTimer = 0; };
    const stopMove = () => { cancelAnimationFrame(moveFrame); moveFrame = 0; moving = false; };
    const schedule = () => {
      stopPlay();
      if (!canPlay()) return;
      playTimer = window.setTimeout(() => {
        playTimer = 0;
        if (!canPlay()) return;
        go(1);
      }, Math.max(3000, resumeAt - Date.now()));
    };
    const pauseForInteraction = () => {
      resumeAt = Date.now() + 10000;
      stopPlay();
      schedule();
    };
    const go = (direction, manual = false) => {
      stopMove();
      stopPlay();
      clearTimeout(manualTimer);
      nativeScrolling = false;
      if (manual) resumeAt = Date.now() + 10000;
      // A repeated click continues from the visible position, not from the
      // previous unfinished destination. Keep subpixel geometry through the loop.
      const from = track.scrollLeft;
      const distance = stride() * direction;
      if (motionQuery.matches) {
        writePosition(from + distance);
        schedule();
        return;
      }
      moving = true;
      let started;
      const frame = timestamp => {
        if (started === undefined) started = timestamp;
        const progress = Math.min((timestamp - started) / 440, 1);
        writePosition(from + distance * (1 - Math.pow(1 - progress, 3)));
        if (progress < 1) moveFrame = requestAnimationFrame(frame);
        else { moveFrame = 0; moving = false; schedule(); }
      };
      moveFrame = requestAnimationFrame(frame);
    };
    const syncManualPosition = () => {
      if (touching || disposed) return;
      nativeScrolling = false;
      // Only rebase after momentum ends; preserve a partial-card swipe exactly.
      writePosition(track.scrollLeft);
      pauseForInteraction();
    };
    const showMoreWhereNeeded = () => {
      // Measure every original before changing any button. Interleaved hidden
      // writes and height reads otherwise force repeated grid layout passes.
      const hiddenStates = originalCopies.map(copy =>
        !copy.classList.contains('is-expanded') && copy.scrollHeight <= copy.clientHeight + 2
      );
      hiddenStates.forEach((hidden, index) => {
        cardGroups[index].forEach(({ button }) => {
          if (button.hidden !== hidden) button.hidden = hidden;
        });
      });
    };

    const refreshMetrics = () => {
      layoutFrame = 0;
      if (disposed || !root.isConnected) return;
      showMoreWhereNeeded();
      const nextStart = position(count);
      const nextWidth = position(count * 2) - nextStart;
      if (nextWidth <= 0 || !track.clientWidth) return;
      if (Math.abs(nextStart - cycleStart) < 0.01 && Math.abs(nextWidth - cycleWidth) < 0.01) return;
      const phase = cycleWidth ? modulo(track.scrollLeft - cycleStart, cycleWidth) / cycleWidth : 0;
      stopMove();
      cycleStart = nextStart;
      cycleWidth = nextWidth;
      writePosition(cycleStart + phase * cycleWidth);
      schedule();
    };
    const requestMetrics = () => {
      if (!layoutFrame && !disposed) layoutFrame = requestAnimationFrame(refreshMetrics);
    };

    root.classList.add('is-ready');
    refreshMetrics();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(requestMetrics);
    root.addEventListener('click', event => {
      const button = event.target.closest('[data-rp-review-more]');
      if (!button || !root.contains(button)) return;
      const card = button.closest('.rp-text-reviews__card');
      if (!card) return;
      const index = Number(card.dataset.rpTextReviewIndex);
      const isExpanded = !originalCopies[index].classList.contains('is-expanded');
      cardGroups[index].forEach(({ copy, button: toggle }) => {
        copy.classList.toggle('is-expanded', isExpanded);
        toggle.setAttribute('aria-expanded', String(isExpanded));
        toggle.textContent = isExpanded ? 'See less' : 'See more';
      });
      stopMove();
      pauseForInteraction();
      requestMetrics();
    }, { signal: listeners.signal });
    previous.addEventListener('click', () => go(-1, true), { signal: listeners.signal });
    next.addEventListener('click', () => go(1, true), { signal: listeners.signal });
    track.addEventListener('keydown', event => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      go(event.key === 'ArrowLeft' ? -1 : 1, true);
    }, { signal: listeners.signal });
    track.addEventListener('scroll', () => {
      if (disposed || (!touching && Math.abs(track.scrollLeft - lastWritten) < 1)) return;
      nativeScrolling = true;
      stopPlay();
      stopMove();
      resumeAt = Date.now() + 10000;
      clearTimeout(manualTimer);
      manualTimer = window.setTimeout(syncManualPosition, 180);
    }, { passive: true, signal: listeners.signal });
    root.addEventListener('pointerenter', event => {
      if (event.pointerType === 'mouse') { hovering = true; stopPlay(); }
    }, { signal: listeners.signal });
    root.addEventListener('pointerleave', event => {
      if (event.pointerType === 'mouse') { hovering = false; pauseForInteraction(); }
    }, { signal: listeners.signal });
    track.addEventListener('wheel', () => { stopMove(); pauseForInteraction(); }, { passive: true, signal: listeners.signal });
    root.addEventListener('pointerdown', () => { touching = true; stopPlay(); stopMove(); }, { passive: true, signal: listeners.signal });
    const releasePointer = () => {
      if (!touching) return;
      touching = false;
      resumeAt = Date.now() + 10000;
      if (nativeScrolling) {
        clearTimeout(manualTimer);
        manualTimer = window.setTimeout(syncManualPosition, 180);
      } else schedule();
    };
    window.addEventListener('pointerup', releasePointer, { signal: listeners.signal });
    window.addEventListener('pointercancel', releasePointer, { signal: listeners.signal });
    root.addEventListener('focusin', () => { focused = true; stopPlay(); }, { signal: listeners.signal });
    root.addEventListener('focusout', () => {
      queueMicrotask(() => {
        if (!root.contains(document.activeElement)) { focused = false; pauseForInteraction(); }
      });
    }, { signal: listeners.signal });
    document.addEventListener('visibilitychange', schedule, { signal: listeners.signal });
    const onMotionChange = () => { stopMove(); schedule(); };
    if (motionQuery.addEventListener) motionQuery.addEventListener('change', onMotionChange, { signal: listeners.signal });
    else motionQuery.addListener?.(onMotionChange);
    window.addEventListener('resize', requestMetrics, { passive: true, signal: listeners.signal });
    const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(requestMetrics) : null;
    resizeObserver?.observe(track);
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => {
        visible = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.3);
        schedule();
      }, { threshold: 0.3 });
      observer.observe(track);
    } else { visible = true; schedule(); }
    document.addEventListener('shopify:section:unload', event => {
      if (!event.target.contains(root)) return;
      disposed = true;
      listeners.abort();
      observer?.disconnect();
      resizeObserver?.disconnect();
      stopPlay();
      stopMove();
      cancelAnimationFrame(layoutFrame);
      clearTimeout(manualTimer);
      if (!motionQuery.removeEventListener) motionQuery.removeListener?.(onMotionChange);
    }, { signal: listeners.signal });
  }

  const init = scope => scope.querySelectorAll('[data-rp-text-reviews]').forEach(setup);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
